
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Clock, DollarSign } from 'lucide-react';

interface TreatmentSearchModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  treatments: any[];
  onAddTreatment: (treatment: any, visitNumber: number) => void;
  currentVisitNumber: number;
  showMemberPricing: boolean;
}

const TreatmentSearchModalV2 = ({
  isOpen,
  onClose,
  treatments,
  onAddTreatment,
  currentVisitNumber,
  showMemberPricing
}: TreatmentSearchModalV2Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const categories = [...new Set(treatments.map(t => t.category))];
  
  const filteredTreatments = treatments.filter(treatment => {
    const matchesSearch = treatment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         treatment.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || treatment.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddTreatment = (treatment: any) => {
    onAddTreatment(treatment, currentVisitNumber);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-juv-deep-chestnut">
            Add Treatment to Visit {currentVisitNumber}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search treatments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-juv-rose-clay/20 focus:border-juv-terracotta"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-juv-rose-clay/20 rounded-md focus:outline-none focus:border-juv-terracotta"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Treatments Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
            {filteredTreatments.map((treatment) => (
              <div
                key={treatment.id}
                className="border border-juv-rose-clay/20 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-juv-deep-chestnut">{treatment.name}</h3>
                    <Badge variant="outline" className="text-xs mt-1">
                      {treatment.category}
                    </Badge>
                  </div>
                  
                  {treatment.description && (
                    <p className="text-sm text-juv-deep-chestnut/70">{treatment.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-3">
                      {treatment.duration_minutes && (
                        <div className="flex items-center space-x-1 text-juv-deep-chestnut/60">
                          <Clock className="h-3 w-3" />
                          <span>{treatment.duration_minutes}min</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right">
                      {showMemberPricing ? (
                        <div>
                          <div className="text-lg font-bold text-juv-terracotta">
                            ${treatment.member_price}
                          </div>
                          {treatment.member_price < treatment.retail_price && (
                            <div className="text-xs text-gray-500 line-through">
                              ${treatment.retail_price}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-juv-terracotta">
                          ${treatment.retail_price}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleAddTreatment(treatment)}
                    className="w-full juv-button-primary"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Visit
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {filteredTreatments.length === 0 && (
            <div className="text-center py-8 text-juv-deep-chestnut/60">
              No treatments found matching your search criteria.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TreatmentSearchModalV2;
